import _ from 'lodash';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, exhaustMap, tap } from 'rxjs/operators';
import { of, concat } from 'rxjs';

import * as DataActions from './data.actions';
import * as ActivityActions from 'app/virtual-event/features/activity/activity.actions';
import { VirtualEventService } from '../../services/virtual-event.service';
import { ToastService } from 'app/toast/services/toast.service';
import { ACTIVITY_TYPE } from '../../virtual-event.enums';
import { SharedService } from '../../services/shared.service';
import { loadVirtualEvent } from './data.actions';
import { Router } from '@angular/router';



@Injectable()
export class DataEffects {

  createVirtualEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.createVirtualEvent),
      exhaustMap((action: any) => {
        const { payload } = action;
        return this.virtualEventService.createVirtualEvent(payload).pipe(
          map(data => {
            this.router.navigateByUrl('/')
            return DataActions.createVirtualEventSuccess({ data });
          }),
          catchError(error => of(DataActions.createVirtualEventFailure({ error }))))
      })
    );
  });

  loadVirtualEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.loadVirtualEvent),
      exhaustMap((action: any) => {
        const { payload } = action;
        return this.virtualEventService.getVirtualEvent(payload).pipe(
          map(data => DataActions.loadVirtualEventSuccess({ data })),
          catchError(error => of(DataActions.loadVirtualEventFailure({ error }))))
      })
    );
  });

  updateVirtualEvent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.updateVirtualEvent),
      exhaustMap((action: any) => {
        const { payload, redirect } = action;
        return this.virtualEventService.updateVirtualEvent(payload).pipe(
          map(data => {
            if(redirect) {
              return DataActions.updateVirtualEventSuccess({ data: { id: null } });
            }
            return DataActions.updateVirtualEventSuccess({ data });
          }),
          catchError(error => of(DataActions.updateVirtualEventFailure({ error }))))
      })
    );
  });

  updateVirtualEventStream$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.updateVirtualEventStream),
      exhaustMap((action: any) => {
        const { payload } = action;
        return this.virtualEventService.updateVirtualEventStream(payload).pipe(
          map(data => DataActions.updateVirtualEventStreamSuccess({ data })),
          catchError(error => of(DataActions.updateVirtualEventStreamFailure({ error }))))
      })
    );
  });

  updateVirtualEventStatus$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.updateVirtualEventStatus),
      exhaustMap((action: any) => {
        const { payload } = action;
        return this.virtualEventService.updateStatus(payload.id, payload.status).pipe(
          map(data => DataActions.updateVirtualEventStatusSuccess({ data })),
          catchError(error => of(DataActions.updateVirtualEventStatusFailure({ error }))))
      })
    );
  });

  updateVirtualEventPresent$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.updateVirtualEventPresent),
      exhaustMap((action: any) => {
        const { payload, payload: { eventId, campaignId, option, clear } } = action;
        return this.virtualEventService.updatePresent(eventId, campaignId, option.id, clear).pipe(
          tap(()=> {
            const message = payload.clear ? "Presenting Event Info": "Campaign Updated";
            return this.toastService.show('success', message, {delay: 1000});
          }),
          map(data => DataActions.updateVirtualEventPresentSuccess({ payload })),
          catchError(error => of(DataActions.updateVirtualEventPresentFailure({ error }))))
      })
    );
  });

  finalizeVirtualEventAuctionItem$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.finalizeVirtualEventAuctionItem),
      exhaustMap((action: any) => {
        const { payload, payload: { vEventId, campaignId, option } } = action;
        return this.virtualEventService.finalizeAuctionItem(vEventId, campaignId, option.id).pipe(
          tap(()=> {
            return this.toastService.show('success', 'Winner Declared', {delay: 1000});
          }),
          map(data => DataActions.finalizeVirtualEventAuctionItemSuccess({ payload })),
          catchError(error => of(DataActions.finalizeVirtualEventAuctionFailure({ error }))))
      })
    );
  });

  finalizeVirtualEventAuction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.finalizeVirtualEventAuction),
      exhaustMap((action: any) => {
        const { payload, payload: { vEventId, campaign } } = action;
        return this.virtualEventService.finalizeAuction(vEventId, campaign.id).pipe(
          map(data => DataActions.finalizeVirtualEventAuctionSuccess({ payload, data })),
          catchError(error => of(DataActions.finalizeVirtualEventAuctionFailure({ error }))))
      })
    );
  });

  finalizeVirtualEventAuctionSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.finalizeVirtualEventAuctionSuccess),
      exhaustMap((action: any) => {
        const { payload } = action;
        return of({
          type: DataActions.closeVirtualEventSilentAuction.type,
          payload
        });
      })
    );
  });

  closeVirtualEventSilentAuction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.closeVirtualEventSilentAuction),
      exhaustMap((action: any) => {
        const { payload, payload: { vEventId, campaign } } = action;
        return this.virtualEventService.closeSilentAuction(vEventId, campaign.id).pipe(
          tap(()=> {
            return this.toastService.show('success', 'Silent Auction Closed', {delay: 1000});
          }),
          map(data => DataActions.closeVirtualEventSilentAuctionSuccess({ payload, data })),
          catchError(error => of(DataActions.closeVirtualEventSilentAuctionFailure({ error }))))
      })
    );
  });

  removeVirtualEventAuctionBid$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.removeVirtualEventAuctionBid),
      exhaustMap((action: any) => {
        const { payload, payload: { eventId, campaign: { id: campaignId }, option, bidId } } = action;
        return this.virtualEventService.removeAuctionBid(bidId).pipe(
          concatMap(data => {
            const action = DataActions.removeVirtualEventAuctionBidSuccess({ payload, data });
            const optionAction = DataActions.updateVirtualEventPresent({
              payload: {
                eventId,
                campaignId,
                option
              }
            });
            return of(action, optionAction);
          }),
          catchError(error => of(DataActions.finalizeVirtualEventAuctionFailure({ error }))))
      })
    );
  });

  showVirtualEventAuctionMulti$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.showVirtualEventAuctionMulti),
      exhaustMap((action: any) => {
        const { payload, payload: { eventId, campaignId, optionId, amount } } = action;
        return this.virtualEventService.showLiveAuctionMulti(eventId, campaignId, optionId, amount).pipe(
          map(data => DataActions.showVirtualEventAuctionMultiSuccess({ payload, data })),
          catchError(error => of(DataActions.showVirtualEventAuctionMultiFailure({ error }))))
      })
    );
  });

  setVirtualEventPresentationMode$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DataActions.setVirtualEventPresentationMode),
      exhaustMap((action: any) => {
        const { payload, payload: { eventId, presentationMode } } = action;
        return this.virtualEventService.setPresentationMode(eventId, presentationMode).pipe(
          map(data => DataActions.setVirtualEventPresentationModeSuccess({ payload, data })),
          catchError(error => of(DataActions.setVirtualEventPresentationModeFailure({ error }))))
      })
    );
  });

  constructor(
    private actions$: Actions,
    private sharedService: SharedService,
    private virtualEventService: VirtualEventService,
    private toastService: ToastService,
    private router: Router
  ) {}

}
